/**
 * Pricing API Transformers
 * Maps between Strapi DTOs and domain types
 */

import type {
  SkuDto,
  VariantDto,
  CategoryDto,
  PricingProfileDto,
  TenantSummaryDto,
  CustomerGroupSummaryDto,
  PriceListSummaryDto,
  UserSummaryDto,
  ProductsQueryParams,
} from './api-types'
import type {
  PricedProduct,
  PricedVariant,
  Category,
  PricingProfile,
  TenantSummary,
  CustomerGroupSummary,
  PriceListSummary,
  UserSummary,
  LocalizedText,
  Media,
  Certificate,
} from '../domain/types'

/**
 * Extract a raw string value from either a Strapi string response or the
 * Zod-coerced `{en, ar}` object (the DTO schema auto-coerces string → {en}).
 */
function extractCurrentString(value: unknown): string | null {
  if (value == null) return null
  if (typeof value === 'string') return value || null
  if (typeof value === 'object') {
    const obj = value as { en?: string; ar?: string }
    return obj.en || obj.ar || null
  }
  return null
}

/**
 * Build a `LocalizedText` for a given field, using the DTO's own locale plus
 * the `localizations` array Strapi returns when the field is populated.
 *
 * This is the correct path for all catalog DTOs: the server returns the base
 * locale's value directly on `dto[field]` and the other locale inside
 * `dto.localizations[]`. We map those into the `{en, ar}` shape the domain
 * layer expects, with fallbacks so a missing translation never renders blank.
 */
function buildLocalizedText(
  parentDto: { locale?: string | null; localizations?: Array<{ locale: string; [key: string]: unknown }> | null },
  fieldName: string,
  directValue: unknown,
): LocalizedText {
  const result: LocalizedText = { en: '' }

  const currentLocale = (parentDto.locale || 'en').split('-')[0]
  const currentValue = extractCurrentString(directValue)

  if (currentValue) {
    if (currentLocale === 'ar') {
      result.ar = currentValue
    } else {
      result.en = currentValue
    }
  }

  if (Array.isArray(parentDto.localizations)) {
    for (const loc of parentDto.localizations) {
      const rawValue = loc?.[fieldName]
      const value = typeof rawValue === 'string' ? rawValue : null
      if (!value) continue
      const locLang = (loc.locale || '').split('-')[0]
      if (locLang === 'ar') {
        result.ar = value
      } else if (locLang === 'en') {
        result.en = value
      }
    }
  }

  // Display fallback: never render blank if at least one locale is present.
  if (!result.en && result.ar) result.en = result.ar
  if (!result.ar) result.ar = result.en

  return result
}

/**
 * Transform Media DTO to domain Media
 */
function transformMediaDto(dto: { url?: string; formats?: Record<string, { url?: string }> | null; alternativeText?: string | null }): Media {
  return {
    url: dto.url,
    formats: dto.formats ?? undefined,
    alternativeText: dto.alternativeText ?? undefined,
  }
}

/**
 * Transform Certificate DTO to domain Certificate
 */
function transformCertificateDto(dto: {
  id: number
  documentId: string
  title: string
  description?: string | null
  file?: { id: number; name: string; url: string; size?: number; mime?: string } | null
}): Certificate {
  return {
    id: dto.id,
    documentId: dto.documentId,
    title: dto.title,
    description: dto.description ?? null,
    file: dto.file
      ? {
          id: dto.file.id,
          name: dto.file.name,
          url: dto.file.url,
          size: dto.file.size ?? 0,
          mime: dto.file.mime ?? 'application/pdf',
        }
      : null,
  }
}

/**
 * Transform Category DTO to domain
 */
export function transformCategoryDto(dto: CategoryDto): Category {
  return {
    id: dto.id,
    documentId: dto.documentId,
    code: dto.code,
    name: buildLocalizedText(dto, 'name', dto.name),
  }
}

/**
 * Transform Variant DTO to domain PricedVariant
 */
export function transformVariantDto(dto: VariantDto): PricedVariant {
  const skuCode = dto.skuCode ?? dto.variantCode ?? dto.documentId
  const packagingType = dto.packagingType ?? dto.packaging ?? null
  const images = Array.isArray(dto.images) ? dto.images.map(transformMediaDto) : []
  const certificates = Array.isArray(dto.certificates) ? dto.certificates.map(transformCertificateDto) : []
  const description = dto.description
    ? buildLocalizedText(dto, 'description', dto.description)
    : null

  return {
    id: dto.id,
    documentId: dto.documentId,
    skuCode,
    name: buildLocalizedText(dto, 'name', dto.name),
    description,
    barcode: dto.barcode ?? null,
    weight: dto.weight ?? null,
    packagingType,
    images,
    certificates,
    isActive: dto.isActive,
    isAvailable: dto.isAvailable ?? true,
    unitPrice: dto.unitPrice ?? null,
    minQuantity: dto.minQuantity ?? null,
    maxQuantity: dto.maxQuantity ?? null,
    minOrderQuantity: dto.minOrderQuantity ?? null,
    maxOrderQuantity: dto.maxOrderQuantity ?? null,
    priceItemDocumentId: dto.priceItemDocumentId ?? null,
    priceType: dto.priceType ?? null,
  }
}

/**
 * Transform SKU DTO to domain PricedProduct
 * Uses Strapi i18n localizations when available
 */
export function transformProductDto(dto: SkuDto): PricedProduct {
  const code = dto.code ?? dto.documentId

  const name = buildLocalizedText(dto, 'name', dto.name)
  const description = dto.description
    ? buildLocalizedText(dto, 'description', dto.description)
    : null

  return {
    id: dto.id,
    documentId: dto.documentId,
    code,
    name,
    description,
    isActive: dto.isActive,
    categories: dto.categories.map(transformCategoryDto),
    variants: dto.variants.map(transformVariantDto),
  }
}

/**
 * Transform products array
 */
export function transformProductsDto(dtos: SkuDto[]): PricedProduct[] {
  return dtos.map(transformProductDto)
}

/**
 * Transform Tenant summary DTO
 */
export function transformTenantSummaryDto(dto: TenantSummaryDto): TenantSummary {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    name: dto.name,
    channel: dto.channel,
  }
}

/**
 * Transform CustomerGroup summary DTO
 */
export function transformCustomerGroupSummaryDto(dto: CustomerGroupSummaryDto): CustomerGroupSummary {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: dto.name,
  }
}

/**
 * Transform PriceList summary DTO
 */
export function transformPriceListSummaryDto(dto: PriceListSummaryDto): PriceListSummary {
  return {
    id: String(dto.id),
    documentId: dto.documentId,
    code: dto.code,
    name: dto.name,
    currency: dto.currency,
  }
}

/**
 * Transform User summary DTO
 */
export function transformUserSummaryDto(dto: UserSummaryDto): UserSummary {
  return {
    id: String(dto.id),
    email: dto.email,
    username: dto.username,
  }
}

/**
 * Transform PricingProfile DTO to domain
 */
export function transformPricingProfileDto(dto: PricingProfileDto): PricingProfile {
  return {
    user: transformUserSummaryDto(dto.user),
    tenant: dto.tenant ? transformTenantSummaryDto(dto.tenant) : null,
    customerGroup: dto.customerGroup ? transformCustomerGroupSummaryDto(dto.customerGroup) : null,
    priceList: dto.priceList ? transformPriceListSummaryDto(dto.priceList) : null,
  }
}

/**
 * Transform filter params to API query string
 */
export function transformFiltersToQuery(params: ProductsQueryParams): URLSearchParams {
  const query = new URLSearchParams()

  if (params.categoryCode) {
    query.set('categoryCode', params.categoryCode)
  }
  if (params.search) {
    query.set('search', params.search)
  }
  if (params.page) {
    query.set('page', String(params.page))
  }
  if (params.pageSize) {
    query.set('pageSize', String(params.pageSize))
  }
  if (params.locale) {
    query.set('locale', params.locale)
  }

  return query
}
